<template>
  <div class="team-billing-history"  v-if="!loading && !error && teamBillings && teamBillings.length > 0">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 class="mb-1">
          <font-awesome-icon icon="receipt" class="me-2" />
          Kauf-Historie
        </h4>
        <p class="text-muted mb-0">Übersicht aller Bereich-Upgrades und Zahlungsaufforderungen/Rechnungen</p>
      </div>
      
      <!-- Filter - nur bei Pagination anzeigen -->
      <div v-if="pagination && pagination.totalPages > 1" class="d-flex gap-2">
        <select v-model="filters.status" class="form-select form-select-sm" style="width: auto;">
          <option value="">Alle Status</option>
          <option value="pending">Ausstehend</option>
          <option value="completed">Aktiv</option>
          <option value="failed">Fehlgeschlagen</option>
        </select>
        
        <select v-model="filters.plan" class="form-select form-select-sm" style="width: auto;">
          <option value="">Alle Pläne</option>
          <option v-for="plan in availablePlans" :key="plan" :value="plan">
            {{ getPlanDisplayName(plan) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Billing List -->
    <div v-else>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Plan</th>
              <th>Betrag</th>
              <th>Status</th>
              <th>Datum</th>
              <th>Laufzeit</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="billing in teamBillings" :key="billing.id">
              <td>
                <div class="fw-semibold">
                  {{ billing.invoiceNumber || billing.reminderInvoiceNumber || '–' }}
                </div>
                <div class="text-muted small">
                  {{ getDocumentLabel(billing) }}
                </div>
              </td>
              <td>
                <span :class="getPlanBadgeClass(billing.plan)" class="badge">
                  {{ getPlanDisplayName(billing.plan) }}
                </span>
              </td>
              <td>
                <strong>{{ formatCurrency(billing.amount) }}</strong>
              </td>
              <td>
                <span :class="getStatusBadgeClass(billing.status)" class="badge">
                  {{ getStatusDisplayName(billing.status) }}
                </span>
              </td>
              <td>
                {{ formatDate(billing.createdAt) }}
              </td>
              <td>
                {{ formatDate(billing.startDate) }} - {{ formatDate(billing.endDate) }}
              </td>
              <td>
                <div class="btn-group btn-group-sm">
                  <button 
                    class="btn btn-outline-primary"
                    @click="downloadPDF(billing)"
                    :disabled="!canDownloadTeamInvoice(billing)"
                    title="PDF herunterladen"
                  >
                    <font-awesome-icon icon="download" />
                  </button>
                  <button 
                    class="btn btn-outline-info"
                    @click="showDetails(billing)"
                    title="Details anzeigen"
                  >
                    <font-awesome-icon icon="eye" />
                  </button>
                  
                  <!-- PayPal Recheck Buttons für ausstehende PayPal-Zahlungen -->
                  <template v-if="shouldShowPayPalButtons(billing)">
                    <button 
                      class="btn btn-outline-warning"
                      @click="recheckPayPalPayment(billing.id)"
                      title="PayPal-Zahlung überprüfen"
                    >
                      <font-awesome-icon icon="sync" />
                    </button>
                    <button 
                      class="btn btn-outline-success"
                      @click="goToPayPal(billing.id)"
                      title="Zu PayPal gehen"
                    >
                      <font-awesome-icon icon="external-link-alt" />
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination && pagination.totalPages > 1" class="d-flex justify-content-center mt-4">
        <nav aria-label="Bereich-Billing Pagination">
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
              <button 
                class="page-link" 
                @click="changePage(pagination.page - 1)"
                :disabled="pagination.page <= 1"
              >
                Zurück
              </button>
            </li>
            
            <li 
              v-for="page in getPageNumbers()" 
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.page }"
            >
              <button 
                class="page-link" 
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </li>
            
            <li class="page-item" :class="{ disabled: pagination.page >= pagination.totalPages }">
              <button 
                class="page-link" 
                @click="changePage(pagination.page + 1)"
                :disabled="pagination.page >= pagination.totalPages"
              >
                Weiter
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Details Modal -->
    <div 
      v-if="selectedBilling" 
      class="modal show" 
      style="display: block;"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Kauf Details
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="selectedBilling = null"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Dokumentinformationen</h6>
                <dl class="row">
                  <dt class="col-sm-4">Nummer:</dt>
                  <dd class="col-sm-8">{{ selectedBilling.invoiceNumber || selectedBilling.reminderInvoiceNumber || '–' }}</dd>

                  <dt class="col-sm-4">Dokumenttyp:</dt>
                  <dd class="col-sm-8">{{ getDocumentLabel(selectedBilling) }}</dd>
                  
                  <dt class="col-sm-4">Plan:</dt>
                  <dd class="col-sm-8">
                    <span :class="getPlanBadgeClass(selectedBilling.plan)" class="badge">
                      {{ getPlanDisplayName(selectedBilling.plan) }}
                    </span>
                  </dd>
                  
                  <dt class="col-sm-4">Status:</dt>
                  <dd class="col-sm-8">
                    <span :class="getStatusBadgeClass(selectedBilling.status)" class="badge">
                      {{ getStatusDisplayName(selectedBilling.status) }}
                    </span>
                  </dd>
                  
                  <dt class="col-sm-4">Betrag:</dt>
                  <dd class="col-sm-8">{{ formatCurrency(selectedBilling.amount) }}</dd>
                  
                  <dt class="col-sm-4">MwSt:</dt>
                  <dd class="col-sm-8">{{ formatCurrency(selectedBilling.vat) }}</dd>
                </dl>
              </div>
              
              <div class="col-md-6">
                <h6>Zeitraum & Tokens</h6>
                <dl class="row">
                  <dt class="col-sm-4">Startdatum:</dt>
                  <dd class="col-sm-8">{{ formatDate(selectedBilling.startDate) }}</dd>
                  
                  <dt class="col-sm-4">Enddatum:</dt>
                  <dd class="col-sm-8">{{ formatDate(selectedBilling.endDate) }}</dd>
                  
                  <dt class="col-sm-4">Tokens/Monat:</dt>
                  <dd class="col-sm-8">{{ selectedBilling.tokensPerMonth }}</dd>
                  
                  <dt class="col-sm-4">Erstellt am:</dt>
                  <dd class="col-sm-8">{{ formatDate(selectedBilling.createdAt) }}</dd>
                </dl>
              </div>
            </div>
            
            <div v-if="selectedBilling.notes" class="mt-3">
              <h6>Notizen</h6>
              <p class="text-muted">{{ selectedBilling.notes }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-primary"
              @click="downloadPDF(selectedBilling)"
              :disabled="!selectedBilling.invoicePath"
            >
              <font-awesome-icon icon="download" class="me-2" />
              PDF herunterladen
            </button>
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="selectedBilling = null"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div 
      v-if="selectedBilling" 
      class="modal-backdrop fade show"
      @click="selectedBilling = null"
    ></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { teamService } from '@/api/teams'
import { teamBillingApi } from '@/api/teamBilling'
import { showSuccess, showError } from '@/services/notification'

const props = defineProps({
  teamId: {
    type: String,
    required: true
  },
  team: {
    type: Object,
    default: null
  }
})


const loading = ref(false)
const error = ref(null)
const teamBillings = ref([])
const pagination = ref(null)
const selectedBilling = ref(null)
const filters = ref({
  status: '',
  plan: ''
})


const availablePlans = computed(() => {
  const plans = new Set()
  teamBillings.value.forEach(billing => {
    if (billing.plan) {
      plans.add(billing.plan)
    }
  })
  return Array.from(plans).sort()
})


const loadTeamBillings = async () => {
  loading.value = true
  error.value = null
  
  try {
    const result = await teamService.getTeamBillings(
      props.teamId, 
      pagination.value?.page || 1, 
      20, 
      filters.value
    )
    
    teamBillings.value = result.teamBillings || []
    pagination.value = result.pagination || null
    
  } catch (err) {
    console.error('Error loading team billings:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Bereich-Billings'
  } finally {
    loading.value = false
  }
}

const changePage = (page) => {
  if (pagination.value) {
    pagination.value.page = page
    loadTeamBillings()
  }
}

const getPageNumbers = () => {
  if (!pagination.value) return []
  
  const pages = []
  const current = pagination.value.page
  const total = pagination.value.totalPages
  

  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  

  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
}

const getDocumentLabel = (billing) => {
  if (billing.method === 'bank_transfer' && !billing.payed) {
    return 'Zahlungsaufforderung'
  }
  return 'Rechnung'
}

const downloadPDF = async (billing) => {
  try {
    const response = await teamService.downloadTeamBillingPDF(billing.id)


    const documentLabel = getDocumentLabel(billing)
    const safeLabel = documentLabel.replace(/\s+/g, '_')
    let filename = `${safeLabel}_${billing.invoiceNumber || billing.reminderInvoiceNumber || billing.id}.pdf`

    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }


    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess(`${documentLabel} wird heruntergeladen`)
  } catch (err) {
    console.error('Error downloading PDF:', err)
    showError('Fehler beim Herunterladen des Dokuments')
  }
}

const recheckPayPalPayment = async (billingId) => {
  try {
    const result = await teamBillingApi.recheckPayPalTeamPayment(billingId)
    
    if (result.status === 'completed') {
      showSuccess('Zahlung erfolgreich bestätigt!')
      await loadTeamBillings()
    } else if (result.status === 'pending') {
      showError('Zahlung ist noch ausstehend. Bitte versuche es später erneut oder gehe zu PayPal.')
    } else {
      showError('Zahlung fehlgeschlagen. Bitte kontaktiere den Support.')
    }
  } catch (error) {
    console.error('PayPal recheck error:', error)
    showError(error.response?.data?.message || 'Fehler beim Überprüfen der PayPal-Zahlung')
  }
}

const canUpgrade = computed(() => {
  return props.team?.status === 'free'
})

const canDownloadTeamInvoice = (billing) => {
  
  
  
  return billing.status === 'completed' || 
         (billing.status === 'pending' && billing.method === 'bank_transfer' && billing.reminderInvoiceNumber)
}

const shouldShowPayPalButtons = (billing) => {
  
  return billing.status === 'pending' && 
         billing.method === 'paypal' && 
         canUpgrade.value
}

const goToPayPal = async (billingId) => {
  try {
    const result = await teamBillingApi.getPayPalTeamRedirectUrl(billingId)
    window.location.href = result.redirectUrl
  } catch (error) {
    console.error('PayPal redirect error:', error)
    showError(error.response?.data?.message || 'Fehler beim Weiterleiten zu PayPal')
  }
}

const showDetails = (billing) => {
  selectedBilling.value = billing
}

const getPlanDisplayName = (plan) => {
  switch (plan) {
    case 'pro': return 'Pro Plan'
    case 'ultimate': return 'Ultimate Plan'
    default: return plan
  }
}

const getPlanBadgeClass = (plan) => {
  switch (plan) {
    case 'pro': return 'bg-primary'
    case 'ultimate': return 'bg-warning text-dark'
    default: return 'bg-secondary'
  }
}

const getStatusDisplayName = (status) => {
  switch (status) {
    case 'pending': return 'Ausstehend'
    case 'completed': return 'Aktiv'
    case 'failed': return 'Fehlgeschlagen'
    default: return status
  }
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending': return 'bg-warning text-dark'
    case 'completed': return 'bg-success'
    case 'failed': return 'bg-danger'
    default: return 'bg-secondary'
  }
}

const formatCurrency = (amount) => {
  if (!amount) return '€0,00'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('de-DE')
}


watch(filters, () => {
  if (pagination.value) {
    pagination.value.page = 1
  }
  loadTeamBillings()
}, { deep: true })


onMounted(() => {
  loadTeamBillings()
})
</script>
